import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import MapComponent from '../components/MapComponent';
import AddressAutocomplete from '../components/AddressAutocomplete';
import IntermediateStops from '../components/IntermediateStops';
import ChatComponent from '../components/ChatComponent';
import RatingModal from '../components/RatingModal';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import SavedCardManager from '../components/SavedCardManager';
import StationCabLogo from '../components/StationCabLogo';
import { 
  Car, MapPin, Navigation, Star, Clock, CreditCard, 
  Menu, User, History, LogOut, Phone, X, Route, MessageCircle,
  Calendar, Gift, Users, Truck, Bookmark, Plus, Trash2, Zap, Bell, Crosshair, Loader2, Wallet, RefreshCw, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const PassengerDashboard = () => {
  const { t } = useTranslation();
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const { permission, requestPermission, notifyDriverAccepted, notifyDriverArrived, notifyRideCompleted, notifyNewMessage, initializeNative, addPushListener, isNative } = usePushNotifications();
  
  const [step, setStep] = useState('idle'); // idle, booking, searching, ride_active
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [activeRide, setActiveRide] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverPath, setDriverPath] = useState([]);
  const [frequentTrips, setFrequentTrips] = useState([]);
  const [showSaveTrip, setShowSaveTrip] = useState(false);
  const [tripName, setTripName] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [completedRideForRating, setCompletedRideForRating] = useState(null);
  const [isLocating, setIsLocating] = useState(true);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [checkingCard, setCheckingCard] = useState(true);
  
  const [pickup, setPickup] = useState({ lat: 48.8566, lng: 2.3522, address: '' });
  const [destination, setDestination] = useState({ lat: 48.8738, lng: 2.2950, address: '' });
  const [stops, setStops] = useState([]); // Intermediate stops
  const [passengers, setPassengers] = useState(1);
  const [vehicleType, setVehicleType] = useState('standard');
  const [referralCode, setReferralCode] = useState(''); // Code parrain chauffeur
  
  // ETA to destination during ride
  const [etaToDestination, setEtaToDestination] = useState(null);
  const [routeToDestination, setRouteToDestination] = useState(null);

  // Keep AudioContext alive for sounds - initialized on first user interaction
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(false);

  // Initialize audio on first user interaction
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      setSoundEnabled(true);
      console.log('Audio initialized');
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  }, []);

  // Add click listener to enable audio
  useEffect(() => {
    const handleInteraction = () => {
      initAudio();
    };
    document.addEventListener('click', handleInteraction, { once: false });
    document.addEventListener('touchstart', handleInteraction, { once: false });
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [initAudio]);

  // Sound functions for passenger notifications
  const playAcceptedSound = useCallback(() => {
    // Method 1: Web Audio API
    const playWebAudio = () => {
      try {
        const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') ctx.resume();
        
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.value = 523;
        osc.type = 'sine';
        gain.gain.value = 0.7;
        osc.start();
        setTimeout(() => { osc.frequency.value = 659; }, 120);
        setTimeout(() => { osc.frequency.value = 784; }, 240);
        setTimeout(() => { osc.frequency.value = 1047; }, 360);
        setTimeout(() => { osc.stop(); }, 500);
      } catch (e) {
        console.log('Web Audio error:', e);
      }
    };

    // Method 2: HTML5 Audio with base64 beep
    const playHTML5Audio = () => {
      try {
        // Short beep sound in base64
        const audio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVBFVkVmbXQgEAAAABAAEARKwAAIhYAQACABAAZGF0YU' + 'AAAAAA'.repeat(100));
        audio.volume = 1.0;
        audio.play().catch(() => {});
      } catch (e) {}
    };

    // Try both methods
    playWebAudio();
    playHTML5Audio();
    setTimeout(playWebAudio, 600);
    
    // Vibrate on mobile
    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
    
    // Request notification permission and show notification with sound
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('🚗 Chauffeur trouvé!', {
        body: 'Votre chauffeur arrive bientôt',
        icon: '/favicon.ico',
        requireInteraction: true,
        silent: false
      });
    }
  }, []);

  const playArrivedSound = useCallback(() => {
    // Method 1: Web Audio API - URGENT SOUND
    const playWebAudio = () => {
      try {
        const ctx = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') ctx.resume();
        
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.value = 880;
        osc.type = 'square';
        gain.gain.value = 0.9;
        osc.start();
        setTimeout(() => { osc.frequency.value = 1100; }, 80);
        setTimeout(() => { osc.frequency.value = 880; }, 160);
        setTimeout(() => { osc.frequency.value = 1100; }, 240);
        setTimeout(() => { osc.frequency.value = 1320; }, 320);
        setTimeout(() => { osc.frequency.value = 1100; }, 400);
        setTimeout(() => { osc.frequency.value = 1320; }, 480);
        setTimeout(() => { osc.stop(); }, 560);
      } catch (e) {
        console.log('Web Audio error:', e);
      }
    };

    // Play multiple times
    playWebAudio();
    setTimeout(playWebAudio, 700);
    setTimeout(playWebAudio, 1400);
    
    // Strong vibration pattern
    if (navigator.vibrate) navigator.vibrate([500, 200, 500, 200, 500, 200, 500]);
    
    // Browser notification with sound
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('🚗 CHAUFFEUR ARRIVÉ!', {
        body: 'Votre chauffeur vous attend! Rejoignez-le maintenant.',
        icon: '/favicon.ico',
        requireInteraction: true,
        silent: false,
        tag: 'driver-arrived'
      });
    }
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Initialize native push notifications
  useEffect(() => {
    const initPushNotifications = async () => {
      const authToken = localStorage.getItem('volt_token');
      if (authToken && isNative && initializeNative) {
        console.log('Initializing native push notifications for passenger...');
        const success = await initializeNative(authToken);
        if (success) {
          console.log('Native push notifications initialized successfully');
        }
      }
    };
    
    initPushNotifications();
  }, [isNative, initializeNative]);

  // Listen for native push notifications
  useEffect(() => {
    if (!isNative || !addPushListener) return;
    
    const unsubscribe = addPushListener((event) => {
      console.log('Native push notification received (passenger):', event);
      // Native notifications are handled by the OS when app is in background
      // When user taps, the app will open and refresh the active ride state
    });
    
    return unsubscribe;
  }, [isNative, addPushListener]);

  // Get user's current location on mount with permission check
  useEffect(() => {
    const checkPermissionAndGetLocation = async () => {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        setIsLocating(false);
        toast.error('Votre navigateur ne supporte pas la géolocalisation');
        return;
      }

      // Check permission status first if API is available
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
          
          if (permissionStatus.state === 'denied') {
            setIsLocating(false);
            toast.error(
              'Géolocalisation refusée. Activez-la dans les paramètres de votre navigateur.',
              { duration: 6000 }
            );
            return;
          }
          
          // Listen for permission changes
          permissionStatus.onchange = () => {
            if (permissionStatus.state === 'granted') {
              getLocation();
            }
          };
        } catch (e) {
          // Permission API not fully supported, continue with geolocation
          console.log('Permission API not supported, trying geolocation directly');
        }
      }
      
      getLocation();
    };

    const getLocation = () => {
      console.log('Requesting geolocation...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          console.log('Geolocation success:', position.coords);
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get address
          try {
            const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
            if (!MAPBOX_TOKEN) {
              console.error('MAPBOX_TOKEN not found');
              setPickup({
                lat: latitude,
                lng: longitude,
                address: 'Position actuelle'
              });
              setIsLocating(false);
              return;
            }
            
            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&language=fr`
            );
            const data = await response.json();
            const address = data.features?.[0]?.place_name || 'Position actuelle';
            
            setPickup({
              lat: latitude,
              lng: longitude,
              address: address
            });
            toast.success('Position détectée', { duration: 2000 });
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            setPickup({
              lat: latitude,
              lng: longitude,
              address: 'Position actuelle'
            });
          }
          setIsLocating(false);
        },
        (error) => {
          console.error('Geolocation error:', error.code, error.message);
          setIsLocating(false);
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              toast.error(
                'Veuillez autoriser la géolocalisation dans votre navigateur',
                { duration: 5000 }
              );
              break;
            case error.POSITION_UNAVAILABLE:
              toast.error('Position non disponible. Vérifiez votre GPS.', { duration: 4000 });
              break;
            case error.TIMEOUT:
              toast.info('Délai dépassé. Cliquez sur le bouton de localisation ou entrez votre adresse.', { duration: 4000 });
              break;
            default:
              toast.info('Entrez votre adresse de départ', { duration: 3000 });
          }
        },
        { 
          enableHighAccuracy: true, 
          timeout: 20000,  // Increased timeout to 20 seconds
          maximumAge: 30000  // Accept cached position up to 30 seconds old
        }
      );
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(() => {
      checkPermissionAndGetLocation();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Submit rating and reset page after
  const submitRating = async (ratingData) => {
    try {
      console.log('Submitting rating:', ratingData);
      const response = await api.post('/ratings', ratingData);
      console.log('Rating submitted successfully:', response.data);
      // Reset page state for new booking
      resetBookingState();
    } catch (error) {
      console.error('Rating submission error:', error.response?.data || error.message);
      throw error; // Re-throw so RatingModal can handle it
    }
  };

  // Check for saved payment card on mount
  useEffect(() => {
    const checkSavedCard = async () => {
      try {
        const response = await api.get('/payments/saved-card');
        if (response.data.has_card) {
          setSavedCard(response.data.card);
        }
      } catch (err) {
        console.log('No saved card or error:', err);
      }
      setCheckingCard(false);
    };
    checkSavedCard();
  }, [api]);

  // Reset page to initial booking state
  const resetBookingState = useCallback(() => {
    setActiveRide(null);
    setStep('idle');
    setEstimate(null);
    setDriverLocation(null);
    setDriverPath([]);
    setDestination({ lat: 48.8738, lng: 2.2950, address: '' });
    setStops([]); // Reset intermediate stops
    setShowRatingModal(false);
    setCompletedRideForRating(null);
    setChatOpen(false);
    setUnreadMessages(0);
    toast.success('Prêt pour une nouvelle course!', { duration: 2000 });
  }, []);

  // Fetch available drivers for map display
  const fetchAvailableDrivers = useCallback(async () => {
    // Don't fetch if we have an active ride (driver assigned)
    if (activeRide) return;
    
    try {
      const response = await api.get('/drivers/available');
      setAvailableDrivers(response.data || []);
    } catch (error) {
      console.error('Error fetching available drivers:', error);
    }
  }, [api, activeRide]);

  // Poll available drivers every 10 seconds
  useEffect(() => {
    fetchAvailableDrivers();
    const interval = setInterval(fetchAvailableDrivers, 10000);
    return () => clearInterval(interval);
  }, [fetchAvailableDrivers]);

  // Fetch frequent trips
  const fetchFrequentTrips = useCallback(async () => {
    try {
      const response = await api.get('/frequent-trips');
      setFrequentTrips(response.data);
    } catch (error) {
      console.error('Error fetching frequent trips:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchFrequentTrips();
  }, [fetchFrequentTrips]);

  // Load a frequent trip
  const loadFrequentTrip = async (trip) => {
    setPickup(trip.pickup);
    setDestination(trip.destination);
    setVehicleType(trip.vehicle_type);
    setPassengers(trip.passenger_count);
    
    // Increment use count
    try {
      await api.post(`/frequent-trips/${trip.id}/use`);
      fetchFrequentTrips();
    } catch (error) {
      console.error('Error updating trip use count:', error);
    }
    
    toast.success(`Trajet "${trip.name}" chargé !`);
  };

  // Save current trip as frequent
  const saveFrequentTrip = async () => {
    if (!tripName.trim()) {
      toast.error('Veuillez entrer un nom pour ce trajet');
      return;
    }
    if (!pickup.address || !destination.address) {
      toast.error('Veuillez remplir les adresses');
      return;
    }
    
    try {
      await api.post('/frequent-trips', {
        name: tripName,
        pickup,
        destination,
        vehicle_type: vehicleType,
        passenger_count: passengers
      });
      toast.success('Trajet enregistré !');
      setShowSaveTrip(false);
      setTripName('');
      fetchFrequentTrips();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Delete a frequent trip
  const deleteFrequentTrip = async (tripId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/frequent-trips/${tripId}`);
      toast.success('Trajet supprimé');
      fetchFrequentTrips();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle route calculation callback
  const handleRouteCalculated = useCallback((info) => {
    setRouteInfo(info);
  }, []);

  // Fetch driver location for active ride
  const fetchDriverLocation = useCallback(async () => {
    if (!activeRide?.id || !activeRide?.driver_id) return;
    
    try {
      // Fetch both location and path
      const [locationRes, pathRes] = await Promise.all([
        api.get(`/rides/${activeRide.id}/driver-location`),
        api.get(`/rides/${activeRide.id}/driver-path`)
      ]);
      
      if (locationRes.data.location) {
        setDriverLocation(locationRes.data.location);
      }
      
      if (pathRes.data.path && pathRes.data.path.length > 0) {
        setDriverPath(pathRes.data.path);
      }
    } catch (error) {
      console.error('Error fetching driver location:', error);
    }
  }, [activeRide?.id, activeRide?.driver_id, api]);

  // Notification handler for real-time updates
  const handleNotification = useCallback((data) => {
    console.log('Passenger notification:', data);
    
    switch (data.type) {
      case 'ride_accepted':
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Chauffeur trouvé!</span>
            <span className="text-sm">{data.driver_name} arrive dans {data.eta_minutes || '~5'} min</span>
          </div>,
          { duration: 5000 }
        );
        // Push notification
        notifyDriverAccepted(data.driver_name, data.eta_minutes || 5);
        // Play sound for ride accepted
        playAcceptedSound();
        setStep('ride_active');
        fetchActiveRide();
        break;
      
      case 'driver_arrived':
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Votre chauffeur est arrivé!</span>
            <span className="text-sm">{data.driver_name} vous attend</span>
          </div>,
          { duration: 8000 }
        );
        // Push notification
        notifyDriverArrived(data.driver_name);
        // Play urgent sound for driver arrived
        playArrivedSound();
        fetchActiveRide();
        break;
        
      case 'ride_started':
        toast.success('La course a démarré!', { duration: 3000 });
        fetchActiveRide();
        break;
        
      case 'ride_completed':
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Course terminée!</span>
            <span className="text-sm">Montant: {data.final_fare}€</span>
          </div>,
          { duration: 5000 }
        );
        // Push notification
        notifyRideCompleted(data.final_fare);
        // Show rating modal after a short delay
        setTimeout(() => {
          setCompletedRideForRating({
            id: data.ride_id,
            driver_name: data.driver_name,
            final_fare: data.final_fare
          });
          setShowRatingModal(true);
        }, 1500);
        fetchActiveRide();
        setDriverLocation(null);
        break;
      
      case 'driver_location':
        // Real-time driver location update
        if (data.location) {
          setDriverLocation(data.location);
        }
        break;
      
      case 'new_message':
        // New chat message notification
        if (!chatOpen) {
          setUnreadMessages(prev => prev + 1);
          toast.info(
            <div className="flex flex-col gap-1">
              <span className="font-semibold">{data.sender_name}</span>
              <span className="text-sm">{data.message}</span>
            </div>,
            { duration: 4000 }
          );
          // Push notification
          notifyNewMessage(data.sender_name, data.message);
        }
        break;
        
      default:
        break;
    }
  }, [notifyDriverAccepted, notifyDriverArrived, notifyRideCompleted, notifyNewMessage, chatOpen, playAcceptedSound, playArrivedSound, resetBookingState]);

  // Connect to notification polling
  useNotifications(api, 'passenger', handleNotification);

  // Poll driver location when ride is active
  useEffect(() => {
    let locationInterval;
    
    if (activeRide && activeRide.driver_id && 
        (activeRide.status === 'accepted' || activeRide.status === 'in_progress')) {
      fetchDriverLocation();
      locationInterval = setInterval(fetchDriverLocation, 5000);
    } else {
      setDriverLocation(null);
      setDriverPath([]);
    }
    
    return () => {
      if (locationInterval) clearInterval(locationInterval);
    };
  }, [activeRide, fetchDriverLocation]);

  // Calculate ETA to destination when ride is in progress
  useEffect(() => {
    const calculateEtaToDestination = async () => {
      if (activeRide && activeRide.status === 'in_progress' && driverLocation && activeRide.destination) {
        try {
          const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
          const destLat = activeRide.destination.lat;
          const destLng = activeRide.destination.lng;
          
          const response = await fetch(
            `https://api.mapbox.com/directions/v5/mapbox/driving/${driverLocation.lng},${driverLocation.lat};${destLng},${destLat}?access_token=${MAPBOX_TOKEN}&geometries=geojson`
          );
          const data = await response.json();
          
          if (data.routes && data.routes[0]) {
            const route = data.routes[0];
            const durationMinutes = Math.round(route.duration / 60);
            const distanceKm = (route.distance / 1000).toFixed(1);
            
            setEtaToDestination({
              minutes: durationMinutes,
              distance: distanceKm
            });
            setRouteToDestination(route.geometry);
          }
        } catch (error) {
          console.error('Error calculating ETA to destination:', error);
        }
      } else if (!activeRide || activeRide.status !== 'in_progress') {
        setEtaToDestination(null);
        setRouteToDestination(null);
      }
    };

    calculateEtaToDestination();
    // Recalculate every 30 seconds during ride
    const etaInterval = activeRide?.status === 'in_progress' ? setInterval(calculateEtaToDestination, 30000) : null;
    
    return () => {
      if (etaInterval) clearInterval(etaInterval);
    };
  }, [activeRide, driverLocation]);

  // Track previous ride status to detect changes
  const [prevRideStatus, setPrevRideStatus] = useState(null);
  const [prevDriverArrived, setPrevDriverArrived] = useState(false);

  useEffect(() => {
    fetchActiveRide();
    // Poll every 3 seconds for faster notification
    const interval = setInterval(fetchActiveRide, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveRide = async () => {
    try {
      const response = await api.get('/rides/active');
      if (response.data) {
        const newRide = response.data;
        const newStatus = newRide.status;
        const driverArrived = newRide.driver_arrived;
        
        // Detect status changes and play sounds
        if (prevRideStatus && prevRideStatus !== newStatus) {
          if (newStatus === 'accepted' && prevRideStatus === 'pending') {
            // Driver accepted the ride!
            console.log('Ride accepted - playing sound');
            playAcceptedSound();
            toast.success(
              <div className="flex flex-col gap-1">
                <span className="font-semibold">🚗 Chauffeur trouvé!</span>
                <span className="text-sm">{newRide.driver_name} arrive dans ~{newRide.driver_eta_minutes || 5} min</span>
              </div>,
              { duration: 8000 }
            );
          } else if (newStatus === 'in_progress' && prevRideStatus === 'accepted') {
            // Ride started
            console.log('Ride started');
            toast.success('🚗 Course démarrée! Bon voyage!', { duration: 5000 });
          }
        }
        
        // Detect driver arrived
        if (driverArrived && !prevDriverArrived && newStatus === 'accepted') {
          console.log('Driver arrived - playing URGENT sound');
          playArrivedSound();
          toast.success(
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-lg">🚗 Votre chauffeur est arrivé!</span>
              <span className="text-sm">{newRide.driver_name} vous attend</span>
              <span className="text-xs text-yellow-500">Rejoignez-le rapidement</span>
            </div>,
            { duration: 15000 }
          );
        }
        
        setPrevRideStatus(newStatus);
        setPrevDriverArrived(driverArrived || false);
        setActiveRide(newRide);
        // Si statut pending (recherche chauffeur), afficher l'écran de recherche
        if (newStatus === 'pending') {
          setStep('searching');
        } else {
          setStep('ride_active');
        }
      } else {
        setPrevRideStatus(null);
        setPrevDriverArrived(false);
        setActiveRide(null);
        if (step === 'ride_active') setStep('idle');
      }
    } catch (error) {
      console.error('Error fetching active ride:', error);
    }
  };

  const getEstimate = async () => {
    if (!pickup.address || !destination.address) {
      toast.error('Veuillez remplir les adresses');
      return;
    }
    
    // Validate that addresses were properly selected (have coordinates)
    if (!pickup.lat || !pickup.lng || pickup.lat === 48.8566 && pickup.lng === 2.3522 && !pickup.address.includes('Paris')) {
      toast.error('Veuillez sélectionner une adresse de départ dans la liste');
      return;
    }
    if (!destination.lat || !destination.lng) {
      toast.error('Veuillez sélectionner une adresse de destination dans la liste');
      return;
    }
    
    // Validate intermediate stops have addresses
    const validStops = stops.filter(s => s.address && s.lat && s.lng);
    if (stops.length > 0 && validStops.length !== stops.length) {
      toast.error('Veuillez remplir toutes les adresses des arrêts intermédiaires');
      return;
    }
    
    // Validate passengers for vehicle type
    if ((vehicleType === 'standard' || vehicleType === 'taxi') && passengers > 4) {
      toast.error('Maximum 4 passagers pour ce type de véhicule. Choisissez un Van.');
      return;
    }
    if (vehicleType === 'van' && passengers > 7) {
      toast.error('Maximum 7 passagers pour un Van.');
      return;
    }
    
    try {
      // Debug: log what we're sending
      console.log('Estimate request:', { 
        pickup, 
        destination, 
        vehicle_type: vehicleType,
        pickup_has_coords: Boolean(pickup.lat && pickup.lng),
        dest_has_coords: Boolean(destination.lat && destination.lng),
        stops_order: stops.map((s, i) => `${i+1}: ${s.address}`)
      });
      
      const response = await api.post('/rides/estimate', { 
        pickup, 
        destination,
        stops: validStops.length > 0 ? validStops : null,
        vehicle_type: vehicleType,
        passenger_count: passengers
      });
      setEstimate(response.data);
      setStep('booking');
    } catch (error) {
      toast.error('Erreur lors de l\'estimation');
    }
  };

  const createRide = async () => {
    // Check if user has a saved card
    if (!savedCard) {
      setShowAddCardModal(true);
      toast.error('Veuillez d\'abord enregistrer un moyen de paiement');
      return;
    }
    
    // Filter valid stops
    const validStops = stops.filter(s => s.address && s.lat && s.lng);
    
    try {
      setLoadingPayment(true);
      
      // AUTHORIZE the payment (reserve funds but don't charge yet)
      // The actual charge happens only when the ride is completed
      const paymentResponse = await api.post('/payments/authorize', {
        amount: Math.round(estimate.estimated_fare * 100), // Convert to cents
        description: `Course StationCab: ${pickup.address} → ${destination.address}`,
        metadata: {
          vehicle_type: vehicleType,
          passenger_count: passengers.toString()
        }
      });
      
      if (paymentResponse.data.success) {
        // Authorization successful, create the ride with the payment_intent_id
        const response = await api.post('/rides', { 
          pickup, 
          destination,
          stops: validStops.length > 0 ? validStops : null,
          vehicle_type: vehicleType,
          passenger_count: passengers,
          payment_status: 'authorized',  // Authorized, not paid yet
          payment_intent_id: paymentResponse.data.payment_intent_id,
          referral_driver_code: referralCode.trim().toUpperCase() || null  // Code parrain
        });
        
        setActiveRide(response.data);
        setStep('searching');
        toast.success('Carte validée ! Recherche d\'un chauffeur...');
        
        // Start polling for driver acceptance or cancellation
        const rideId = response.data.id;
        const checkInterval = setInterval(async () => {
          try {
            const check = await api.get(`/rides/${rideId}`);
            const status = check.data.status;
            
            if (status === 'accepted' || status === 'arrived' || status === 'in_progress') {
              clearInterval(checkInterval);
              setActiveRide(check.data);
              setStep('ride_active');
              toast.success('Chauffeur trouvé!');
            } else if (status === 'cancelled') {
              // Ride was cancelled (by system or all drivers refused)
              // Authorization is automatically released - no charge
              clearInterval(checkInterval);
              setActiveRide(null);
              setStep('idle');
              setEstimate(null);
              toast.info('Course annulée - Aucun prélèvement effectué.');
            }
          } catch (err) {
            console.error('Error checking ride:', err);
            // If ride not found, reset to idle
            if (err.response?.status === 404) {
              clearInterval(checkInterval);
              setActiveRide(null);
              setStep('idle');
              setEstimate(null);
            }
          }
        }, 3000);
        
        // Clear after 2 minutes if no driver found and cancel the ride
        setTimeout(async () => {
          clearInterval(checkInterval);
          // Check current status before cancelling
          try {
            const finalCheck = await api.get(`/rides/${rideId}`);
            if (finalCheck.data.status === 'pending') {
              // Still pending after 2 minutes, cancel it
              // This will release the authorization - no charge
              await api.post(`/rides/${rideId}/cancel`);
              setActiveRide(null);
              setStep('idle');
              setEstimate(null);
              toast.error('Aucun chauffeur disponible. Votre carte n\'a pas été débitée.');
            }
          } catch (err) {
            console.error('Error in timeout check:', err);
            setActiveRide(null);
            setStep('idle');
          }
        }, 120000);
      }
    } catch (error) {
      console.error('Authorization error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la validation de la carte';
      toast.error(errorMsg);
      
      // If card error, suggest to add a new card
      if (errorMsg.includes('carte') || errorMsg.includes('card')) {
        setShowAddCardModal(true);
      }
    }
    setLoadingPayment(false);
  };
  
  // Callback when card is saved
  const handleCardSaved = async () => {
    setShowAddCardModal(false);
    // Refresh saved card info
    try {
      const response = await api.get('/payments/saved-card');
      if (response.data.has_card) {
        setSavedCard(response.data.card);
        toast.success('Carte enregistrée ! Vous pouvez maintenant réserver.');
      }
    } catch (err) {
      console.error('Error refreshing card:', err);
    }
  };

  const cancelRide = async () => {
    if (!activeRide) return;
    
    // Check if cancellation fee applies (ride accepted/arrived/in_progress)
    const hasDriver = activeRide.driver_id && ['accepted', 'arrived', 'in_progress'].includes(activeRide.status);
    
    if (hasDriver) {
      // Calculate fee based on vehicle type
      const fee = activeRide.vehicle_type === 'van' ? 15 : 8;
      const confirmed = window.confirm(
        `⚠️ Attention: Votre chauffeur a déjà accepté la course.\n\n` +
        `Des frais d'annulation de ${fee}€ seront prélevés sur votre carte enregistrée.\n\n` +
        `Voulez-vous vraiment annuler ?`
      );
      if (!confirmed) return;
    }
    
    try {
      const response = await api.post(`/rides/${activeRide.id}/cancel`);
      setActiveRide(null);
      setStep('idle');
      setEstimate(null);
      
      // Show appropriate message based on fee
      if (response.data?.cancellation_fee > 0) {
        if (response.data?.cancellation_fee_charged) {
          toast.success(`Course annulée. Frais d'annulation: ${response.data.cancellation_fee}€`);
        } else {
          toast.warning(`Course annulée. Frais d'annulation de ${response.data.cancellation_fee}€ non débités (problème de paiement)`);
        }
      } else {
        toast.success('Course annulée');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handlePayment = async () => {
    if (!activeRide) return;
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    toast.success('Paiement effectué avec succès !');
    setShowPaymentModal(false);
    // Refresh ride data
    fetchActiveRide();
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
  };

  const handlePaymentError = (errorMsg) => {
    toast.error(errorMsg || 'Erreur de paiement');
  };

  const rateDriver = async (rating) => {
    if (!activeRide) return;
    try {
      await api.post('/ratings', { ride_id: activeRide.id, rating });
      toast.success('Merci pour votre évaluation!');
      setActiveRide(null);
      setStep('idle');
    } catch (error) {
      toast.error('Erreur lors de l\'évaluation');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'text-yellow-500';
      case 'accepted': return 'text-blue-500';
      case 'arrived': return 'text-orange-500';
      case 'in_progress': return 'text-green-500';
      case 'completed': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Recherche d\'un chauffeur...';
      case 'accepted': return 'Chauffeur en route vers vous';
      case 'arrived': return 'Votre chauffeur est arrivé';
      case 'in_progress': return 'Course en cours';
      case 'completed': return 'Course terminée';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass p-4">
        <div className="flex items-center justify-between">
          <StationCabLogo size="small" darkMode={true} />
          
          <div className="flex items-center gap-2">
            {/* Refresh button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={async () => {
                try {
                  // Show loading indicator
                  const toastId = toast.loading('Actualisation...');
                  
                  // Refresh active ride
                  const rideResponse = await api.get('/rides/active');
                  if (rideResponse.data) {
                    setActiveRide(rideResponse.data);
                    setStep('ride_active');
                  } else {
                    setActiveRide(null);
                    // Refresh estimate if in booking step
                    if (step === 'booking' && pickup?.lat && destination?.lat) {
                      await getEstimate();
                    }
                  }
                  
                  // Refresh favorites
                  try {
                    const favResponse = await api.get('/favorites');
                    setFavorites(favResponse.data || []);
                  } catch (e) {}
                  
                  // Refresh wallet balance
                  try {
                    const walletResponse = await api.get('/wallet/balance');
                    setWalletBalance(walletResponse.data?.balance || 0);
                  } catch (e) {}
                  
                  toast.dismiss(toastId);
                  toast.success('Page actualisée');
                } catch (error) {
                  console.error('Refresh error:', error);
                  toast.error('Erreur lors du rafraîchissement');
                }
              }}
              className="rounded-full"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="menu-btn" className="rounded-full">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-card border-border">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Star className="w-3 h-3 fill-primary text-primary" />
                        {user?.rating?.toFixed(1)}
                      </p>
                    </div>
                  </div>
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-8 space-y-2">
                {permission !== 'granted' && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-12 border-primary/50 text-primary"
                    onClick={requestPermission}
                    data-testid="enable-notifications"
                  >
                    <Bell className="w-5 h-5 mr-3" /> Activer les notifications
                  </Button>
                )}
                <Link to="/profile" onClick={() => setMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start h-12" data-testid="nav-profile">
                    <User className="w-5 h-5 mr-3" /> Mon profil
                  </Button>
                </Link>
                <Link to="/scheduled" onClick={() => setMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start h-12" data-testid="nav-scheduled">
                    <Calendar className="w-5 h-5 mr-3" /> Courses planifiées
                  </Button>
                </Link>
                <Link to="/history" onClick={() => setMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start h-12" data-testid="nav-history">
                    <History className="w-5 h-5 mr-3" /> Historique
                  </Button>
                </Link>
                <Link to="/payments" onClick={() => setMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start h-12" data-testid="nav-payments">
                    <Gift className="w-5 h-5 mr-3" /> Paiements & Promos
                  </Button>
                </Link>
                <Link to="/wallet" onClick={() => setMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start h-12" data-testid="nav-wallet">
                    <Wallet className="w-5 h-5 mr-3" /> Mon Portefeuille
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-12 text-destructive hover:text-destructive"
                  onClick={logout}
                  data-testid="nav-logout"
                >
                  <LogOut className="w-5 h-5 mr-3" /> Déconnexion
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
          </div>
        </div>
      </header>

      {/* Map Area */}
      <div className="h-screen pt-20">
        <MapComponent 
          pickupLocation={activeRide ? activeRide.pickup : (pickup.address ? pickup : null)}
          destinationLocation={activeRide ? activeRide.destination : (destination.address ? destination : null)}
          driverLocation={driverLocation}
          driverPath={driverPath}
          availableDrivers={!activeRide ? availableDrivers : []}
          onRouteCalculated={handleRouteCalculated}
          className="absolute inset-0"
        />
        
        {/* Driver Location Badge - Show when tracking driver */}
        {driverLocation && activeRide && (
          <div className="absolute top-24 left-4 z-30">
            <div className="glass rounded-xl p-3 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center animate-pulse">
                <Car className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {activeRide.status === 'accepted' ? 'Chauffeur en route' : 'Course en cours'}
                </p>
                <p className="font-semibold text-blue-400">
                  {activeRide.driver_eta_minutes && activeRide.status === 'accepted' 
                    ? `Arrivée dans ~${activeRide.driver_eta_minutes} min`
                    : 'Position en temps réel'
                  }
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Auto-assigned driver badge - Show immediately after booking */}
        {activeRide && activeRide.status === 'accepted' && !driverLocation && (
          <div className="absolute top-24 left-4 right-4 z-30">
            <div className="glass rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Car className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Chauffeur attribué</p>
                  <p className="font-semibold text-green-400">{activeRide.driver_name}</p>
                </div>
              </div>
              {activeRide.driver_eta_minutes && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Arrivée estimée</p>
                  <p className="font-semibold text-primary">{activeRide.driver_eta_minutes} min</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* ETA to destination badge - Show during ride in progress */}
        {activeRide && activeRide.status === 'in_progress' && etaToDestination && (
          <div className="absolute top-24 left-4 right-4 z-30">
            <div className="glass rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center animate-pulse">
                  <Navigation className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Course en cours</p>
                  <p className="font-semibold text-blue-400">{etaToDestination.distance} km restants</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Arrivée dans</p>
                <p className="font-semibold text-primary text-xl">{etaToDestination.minutes} min</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Route Info Badge */}
        {routeInfo && pickup.address && destination.address && step === 'idle' && !activeRide && (
          <div className="absolute top-24 left-4 right-4 z-30">
            <div className="glass rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Route className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Itinéraire</p>
                  <p className="font-semibold">{routeInfo.distance} km</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Temps estimé</p>
                <p className="font-semibold text-primary">{routeInfo.duration} min</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Panel */}
      <div className="fixed bottom-0 left-0 right-0 mobile-drawer glass p-6 z-40">
        {step === 'idle' && (
          <div className="space-y-4 animate-fade-in">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Où allez-vous?</h2>
            
            <div className="space-y-3">
              {/* Location indicator */}
              {isLocating && (
                <div className="flex items-center gap-3 text-sm bg-primary/10 border border-primary/30 p-3 rounded-xl">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-primary">Localisation en cours...</p>
                    <p className="text-xs text-muted-foreground">Veuillez autoriser l'accès à votre position</p>
                  </div>
                </div>
              )}
              
              <div className="relative">
                <AddressAutocomplete
                  value={pickup}
                  onChange={setPickup}
                  placeholder="Point de départ"
                  icon={MapPin}
                  iconColor="text-green-500"
                  dataTestId="input-pickup"
                />
                {/* Relocate button */}
                <button
                  onClick={() => {
                    if (navigator.geolocation) {
                      setIsLocating(true);
                      toast.info('Localisation en cours...', { duration: 2000 });
                      navigator.geolocation.getCurrentPosition(
                        async (position) => {
                          const { latitude, longitude } = position.coords;
                          try {
                            const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
                            const response = await fetch(
                              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&language=fr`
                            );
                            const data = await response.json();
                            const address = data.features?.[0]?.place_name || 'Position actuelle';
                            setPickup({ lat: latitude, lng: longitude, address });
                            toast.success('Position mise à jour');
                          } catch (error) {
                            setPickup({ lat: latitude, lng: longitude, address: 'Position actuelle' });
                            toast.success('Position détectée');
                          }
                          setIsLocating(false);
                        },
                        (error) => {
                          console.error('Geolocation error:', error);
                          toast.error('Impossible d\'obtenir votre position. Vérifiez les permissions.');
                          setIsLocating(false);
                        },
                        { enableHighAccuracy: true, timeout: 15000 }
                      );
                    } else {
                      toast.error('Géolocalisation non supportée');
                    }
                  }}
                  disabled={isLocating}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isLocating 
                      ? 'bg-primary/20 cursor-wait' 
                      : 'bg-primary/10 hover:bg-primary/30 hover:scale-110'
                  }`}
                  title="Me localiser"
                  data-testid="relocate-btn"
                >
                  {isLocating ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : (
                    <Crosshair className="w-5 h-5 text-primary" />
                  )}
                </button>
              </div>
              
              <AddressAutocomplete
                value={destination}
                onChange={setDestination}
                placeholder="Destination"
                icon={Navigation}
                iconColor="text-primary"
                dataTestId="input-destination"
              />
              
              {/* Intermediate Stops */}
              <div className="pt-2">
                <IntermediateStops 
                  stops={stops} 
                  setStops={setStops} 
                  maxStops={3} 
                />
              </div>
            </div>

            {/* Vehicle Type & Passengers Selection */}
            <div className="grid grid-cols-2 gap-3">
              {/* Vehicle Type */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <Car className="w-4 h-4" /> Type de véhicule
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setVehicleType('standard');
                      if (passengers > 4) setPassengers(4);
                    }}
                    data-testid="vehicle-standard"
                    className={`py-3 px-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                      vehicleType === 'standard' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-white/10 bg-muted/50 hover:border-white/20'
                    }`}
                  >
                    <Car className="w-5 h-5" />
                    <span className="text-xs font-medium">VTC</span>
                    <span className="text-[10px] text-muted-foreground">1-4 places</span>
                  </button>
                  <button
                    onClick={() => setVehicleType('van')}
                    data-testid="vehicle-van"
                    className={`py-3 px-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                      vehicleType === 'van' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-white/10 bg-muted/50 hover:border-white/20'
                    }`}
                  >
                    <Truck className="w-5 h-5" />
                    <span className="text-xs font-medium">Van</span>
                    <span className="text-[10px] text-muted-foreground">1-7 places</span>
                  </button>
                  <button
                    onClick={() => {
                      setVehicleType('taxi');
                      if (passengers > 4) setPassengers(4);
                    }}
                    data-testid="vehicle-taxi"
                    className={`py-3 px-2 rounded-xl border transition-all flex flex-col items-center gap-1 relative ${
                      vehicleType === 'taxi' 
                        ? 'border-yellow-500 bg-yellow-500/10 text-yellow-500' 
                        : 'border-white/10 bg-muted/50 hover:border-white/20'
                    }`}
                  >
                    <div className="absolute -top-2 -right-1 px-1.5 py-0.5 bg-yellow-500 text-[8px] text-black font-bold rounded">
                      OFFICIEL
                    </div>
                    <Car className="w-5 h-5" />
                    <span className="text-xs font-medium">Taxi</span>
                    <span className="text-[10px] text-muted-foreground">Tarif réglementé</span>
                  </button>
                </div>
              </div>

              {/* Passengers Count */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" /> Passagers
                </label>
                <div className="flex items-center justify-center gap-3 h-[76px] bg-muted/50 rounded-xl border border-white/10">
                  <button
                    onClick={() => setPassengers(Math.max(1, passengers - 1))}
                    data-testid="passengers-minus"
                    disabled={passengers <= 1}
                    className="w-10 h-10 rounded-full bg-background border border-white/10 flex items-center justify-center text-xl font-bold disabled:opacity-30 hover:border-primary transition-colors"
                  >
                    -
                  </button>
                  <span className="text-2xl font-bold w-8 text-center" data-testid="passengers-count">{passengers}</span>
                  <button
                    onClick={() => setPassengers(Math.min(vehicleType === 'van' ? 7 : 4, passengers + 1))}
                    data-testid="passengers-plus"
                    disabled={passengers >= (vehicleType === 'van' ? 7 : 4)}
                    className="w-10 h-10 rounded-full bg-background border border-white/10 flex items-center justify-center text-xl font-bold disabled:opacity-30 hover:border-primary transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Taxi tariff info */}
            {vehicleType === 'taxi' && (
              <div className="space-y-2">
                <div className="text-xs bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-yellow-500 font-medium mb-1">
                    <Car className="w-4 h-4" />
                    <span>Taxi Parisien - Tarif Réglementé</span>
                  </div>
                  <p className="text-yellow-500/80 font-medium">
                    💡 Le prix affiché est une estimation. Le montant final sera celui du compteur.
                  </p>
                </div>
                
                {/* Airport flat rates info */}
                <div className="text-xs bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-3">
                  <div className="flex items-center gap-2 text-green-400 font-medium mb-2">
                    <span className="text-base">✈️</span>
                    <span>Forfaits Aéroports - Prix Fixes Garantis</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    <div className="bg-black/20 rounded px-2 py-1.5">
                      <p className="text-green-400 font-medium text-[11px] mb-1">Paris ↔ CDG</p>
                      <p>Rive Droite: <span className="text-white font-medium">56€</span></p>
                      <p>Rive Gauche: <span className="text-white font-medium">65€</span></p>
                    </div>
                    <div className="bg-black/20 rounded px-2 py-1.5">
                      <p className="text-green-400 font-medium text-[11px] mb-1">Paris ↔ Orly</p>
                      <p>Rive Droite: <span className="text-white font-medium">45€</span></p>
                      <p>Rive Gauche: <span className="text-white font-medium">36€</span></p>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2 italic">
                    Ces forfaits s'appliquent pour les trajets directs entre Paris intra-muros et les aéroports
                  </p>
                </div>
              </div>
            )}

            {/* Info about extra passenger fee */}
            {passengers > 4 && (
              <div className="text-xs text-yellow-500 bg-yellow-500/10 rounded-lg px-3 py-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Supplément de 4€ par passager au-delà de 4</span>
              </div>
            )}

            {/* Referral Code Field */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Gift className="w-4 h-4" /> Code chauffeur (optionnel)
              </label>
              <Input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Ex: SC-0001"
                className="h-12 bg-muted/50 border-white/10 rounded-xl uppercase"
                data-testid="referral-code-input"
                maxLength={10}
              />
              <p className="text-[10px] text-muted-foreground">
                Si un chauffeur vous a recommandé StationCab, entrez son code ici
              </p>
            </div>

            {/* Frequent Trips Section */}
            {frequentTrips.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" /> Trajets fréquents
                  </p>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                  {frequentTrips.slice(0, 4).map((trip) => (
                    <div
                      key={trip.id}
                      onClick={() => loadFrequentTrip(trip)}
                      className="flex-shrink-0 bg-muted/50 hover:bg-muted border border-white/10 hover:border-primary/50 rounded-xl px-3 py-2 text-left transition-all group relative cursor-pointer"
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => deleteFrequentTrip(trip.id, e)}
                        onKeyDown={(e) => e.key === 'Enter' && deleteFrequentTrip(trip.id, e)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <X className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm font-medium truncate max-w-[140px]">{trip.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {trip.vehicle_type === 'van' ? <Truck className="w-3 h-3" /> : <Car className="w-3 h-3" />}
                        <span>{trip.passenger_count}p</span>
                        {trip.use_count > 0 && <span className="text-primary">• {trip.use_count}×</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Save Trip / Estimate Buttons */}
            <div className="flex gap-2">
              {pickup.address && destination.address && (
                <Button 
                  variant="outline"
                  onClick={() => setShowSaveTrip(!showSaveTrip)}
                  data-testid="save-trip-btn"
                  className="h-14 px-4 rounded-full"
                >
                  <Bookmark className="w-5 h-5" />
                </Button>
              )}
              <Button 
                onClick={getEstimate}
                data-testid="estimate-btn"
                className="flex-1 h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold text-lg"
              >
                Estimer le prix
              </Button>
            </div>

            {/* Save Trip Form */}
            {showSaveTrip && (
              <div className="bg-muted/50 rounded-xl p-4 space-y-3 animate-fade-in border border-white/10">
                <p className="text-sm font-medium">Enregistrer ce trajet</p>
                <input
                  type="text"
                  value={tripName}
                  onChange={(e) => setTripName(e.target.value)}
                  placeholder="Ex: Maison → Travail"
                  className="w-full px-4 py-3 bg-background border border-white/10 rounded-xl text-sm focus:border-primary outline-none"
                  data-testid="trip-name-input"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowSaveTrip(false); setTripName(''); }}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={saveFrequentTrip}
                    data-testid="confirm-save-trip"
                    className="flex-1 bg-primary text-primary-foreground"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'booking' && estimate && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Estimation</h2>
              <Button variant="ghost" size="icon" onClick={() => { setStep('idle'); setEstimate(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <Card className="bg-muted/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      vehicleType === 'taxi' ? 'bg-yellow-500/20' : 'bg-primary/20'
                    }`}>
                      {vehicleType === 'van' ? (
                        <Truck className="w-5 h-5 text-primary" />
                      ) : vehicleType === 'taxi' ? (
                        <Car className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <Car className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">
                        {vehicleType === 'taxi' ? 'Taxi Parisien' : vehicleType === 'van' ? 'StationCab Van' : 'StationCab VTC'}
                        {estimate.fare_details?.is_airport_flat_rate && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                            ✈️ Forfait Aéroport
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {routeInfo?.distance || estimate.distance_km} km • {estimate.duration_minutes || routeInfo?.duration} min • {passengers} passager{passengers > 1 ? 's' : ''}
                        {estimate.stops_count > 0 && ` • ${estimate.stops_count} arrêt${estimate.stops_count > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  <p className={`text-2xl font-bold ${vehicleType === 'taxi' ? 'text-yellow-500' : 'text-primary'}`}>
                    {estimate.estimated_fare}€
                  </p>
                </div>
                
                {/* Taxi airport flat rate notice */}
                {vehicleType === 'taxi' && estimate.fare_details?.is_airport_flat_rate && (
                  <div className="mb-4 px-3 py-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">✈️</span>
                      <p className="text-sm text-green-400 font-bold">
                        Forfait Aéroport Garanti
                      </p>
                      <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                        PRIX FIXE
                      </span>
                    </div>
                    <p className="text-xs text-green-300 mb-2">
                      {estimate.fare_details.direction_label}
                    </p>
                    <div className="flex items-center justify-between bg-black/20 rounded px-3 py-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Forfait officiel {estimate.fare_details.rive_label}</p>
                        <p className="text-lg font-bold text-green-400">{estimate.fare_details.flat_rate}€</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">+ {estimate.fare_details.booking_supplement_label}</p>
                        <p className="text-lg font-bold text-yellow-500">+{estimate.fare_details.booking_supplement}€</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 italic">
                      {estimate.fare_details.regulation_text}
                    </p>
                  </div>
                )}
                
                {/* Taxi regulation notice (non-airport) */}
                {vehicleType === 'taxi' && estimate.fare_details?.regulated && !estimate.fare_details?.is_airport_flat_rate && (
                  <div className="mb-4 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-xs text-yellow-500 font-medium mb-1">
                      ⚠️ Prix estimatif - Tarification au compteur
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Le prix final sera celui affiché au compteur du taxi à la fin de la course. 
                      Ce montant peut varier selon le trafic et le temps réel du trajet.
                    </p>
                  </div>
                )}
                
                {/* Route with stops */}
                {stops.length > 0 && (
                  <div className="mb-4 pb-3 border-b border-white/10">
                    <p className="text-xs text-muted-foreground mb-2">Itinéraire</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-[10px] font-bold text-white">1</div>
                        <span className="truncate">{pickup.address}</span>
                      </div>
                      {stops.map((stop, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-amber-500">
                          <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-[10px] font-bold text-white">{idx + 2}</div>
                          <span className="truncate">{stop.address}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-[10px] font-bold text-primary-foreground">{stops.length + 2}</div>
                        <span className="truncate">{destination.address}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Fare breakdown - Only show for VTC, not for taxis */}
                {estimate.fare_details && !estimate.fare_details.is_airport_flat_rate && vehicleType !== 'taxi' && (
                  <div className="space-y-2 pt-3 border-t border-white/10 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Prise en charge</span>
                      <span>{estimate.fare_details.prise_en_charge}€</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Distance ({estimate.distance_km} km)</span>
                      <span>{estimate.fare_details.distance_cost}€</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Temps ({estimate.duration_minutes} min)</span>
                      <span>{estimate.fare_details.time_cost}€</span>
                    </div>
                    {estimate.fare_details.supplement_details?.map((sup, idx) => (
                      <div key={idx} className="flex justify-between text-yellow-500">
                        <span>{sup.name}</span>
                        <span>+{sup.amount}€</span>
                      </div>
                    ))}
                    {estimate.fare_details.minimum_applied && (
                      <div className="flex justify-between text-yellow-500 text-xs">
                        <span>Tarif minimum appliqué</span>
                        <span>8,00€</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Airport flat rate - simplified breakdown */}
                {estimate.fare_details?.is_airport_flat_rate && (
                  <div className="space-y-2 pt-3 border-t border-white/10 text-sm">
                    {estimate.fare_details.supplement_details?.map((sup, idx) => (
                      <div key={idx} className={`flex justify-between ${idx === 0 ? 'text-green-400' : 'text-yellow-500'}`}>
                        <span>{sup.name}</span>
                        <span>{idx === 0 ? '' : '+'}{sup.amount}€</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold text-white pt-2 border-t border-white/10">
                      <span>Total</span>
                      <span>{estimate.fare_details.total}€</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Saved Card Info or Add Card Button */}
            {!savedCard ? (
              <div 
                onClick={() => setShowAddCardModal(true)}
                className="flex items-center justify-between p-4 mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl cursor-pointer hover:bg-yellow-500/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-sm font-medium text-yellow-500">Aucune carte enregistrée</p>
                    <p className="text-xs text-muted-foreground">Ajoutez une carte pour réserver</p>
                  </div>
                </div>
                <CreditCard className="w-5 h-5 text-yellow-500" />
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 mb-4 bg-muted/30 rounded-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">{savedCard.brand} •••• {savedCard.last4}</p>
                    <p className="text-xs text-muted-foreground">Expire {savedCard.exp_month}/{savedCard.exp_year}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddCardModal(true)}
                  className="text-xs"
                >
                  Modifier
                </Button>
              </div>
            )}
            
            <Button 
              onClick={createRide}
              disabled={loadingPayment || !savedCard}
              data-testid="book-ride-btn"
              className={`w-full h-14 hover:opacity-90 rounded-full font-bold text-lg pulse-glow ${
                vehicleType === 'taxi' 
                  ? 'bg-yellow-500 text-black' 
                  : 'bg-primary text-primary-foreground'
              } ${!savedCard ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loadingPayment ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Paiement en cours...
                </span>
              ) : !savedCard ? (
                <span className="flex items-center justify-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Ajoutez une carte pour réserver
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Payer {estimate?.estimated_fare}€ et réserver
                </span>
              )}
            </Button>
          </div>
        )}

        {step === 'searching' && (
          <div className="space-y-4 animate-fade-in text-center py-4">
            <div className="w-16 h-16 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Recherche en cours...</h2>
            <p className="text-muted-foreground">Nous cherchons un chauffeur près de vous</p>
            <Button 
              variant="outline"
              onClick={cancelRide}
              data-testid="cancel-search-btn"
              className="mt-4"
            >
              Annuler
            </Button>
          </div>
        )}

        {step === 'ride_active' && activeRide && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${getStatusColor(activeRide.status)}`}>
                  {getStatusText(activeRide.status)}
                </p>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                  {activeRide.status === 'completed' ? 'Course terminée!' : 'Votre course'}
                </h2>
              </div>
              {activeRide.status !== 'completed' && (
                <Button variant="outline" size="sm" onClick={cancelRide} data-testid="cancel-ride-btn">
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
              )}
            </div>

            {activeRide.driver_name && (
              <Card className="bg-muted/50 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{activeRide.driver_name}</p>
                        <p className="text-sm text-muted-foreground">Votre chauffeur</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Chat Button */}
                      {(activeRide.status === 'accepted' || activeRide.status === 'arrived' || activeRide.status === 'in_progress') && (
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="rounded-full relative" 
                          data-testid="chat-btn"
                          onClick={() => { setChatOpen(true); setUnreadMessages(0); }}
                        >
                          <MessageCircle className="w-5 h-5" />
                          {unreadMessages > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                              {unreadMessages}
                            </span>
                          )}
                        </Button>
                      )}
                      <Button variant="outline" size="icon" className="rounded-full" data-testid="call-driver-btn">
                        <Phone className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{activeRide.distance_km} km</span>
              </div>
              <p className="text-xl font-bold text-primary">
                {activeRide.final_fare || activeRide.estimated_fare}€
              </p>
            </div>

            {activeRide.status === 'completed' && activeRide.payment_status !== 'paid' && (
              <Button 
                onClick={handlePayment}
                disabled={loadingPayment}
                data-testid="pay-btn"
                className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold text-lg"
              >
                {loadingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Chargement...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" /> Payer {activeRide.final_fare || activeRide.estimated_fare}€
                  </>
                )}
              </Button>
            )}

            {activeRide.status === 'completed' && activeRide.payment_status === 'paid' && (
              <div className="space-y-3">
                <Button 
                  onClick={() => {
                    setCompletedRideForRating(activeRide);
                    setShowRatingModal(true);
                  }}
                  data-testid="open-rating-btn"
                  className="w-full h-14 bg-primary/10 text-primary hover:bg-primary/20 rounded-full font-bold"
                >
                  <Star className="w-5 h-5 mr-2" /> Évaluer votre chauffeur
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rating Modal */}
      <RatingModal
        ride={completedRideForRating}
        isOpen={showRatingModal}
        onClose={() => {
          setShowRatingModal(false);
          setCompletedRideForRating(null);
          // Reset for new booking after closing rating modal
          resetBookingState();
        }}
        onSubmit={submitRating}
      />

      {/* Chat Component */}
      <ChatComponent
        api={api}
        rideId={activeRide?.id}
        currentUserRole="passenger"
        isOpen={chatOpen}
        onOpenChange={(open) => { setChatOpen(open); if (open) setUnreadMessages(0); }}
      />

      {/* Payment Modal */}
      {showPaymentModal && activeRide && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md animate-scale-in">
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-2 -right-2 z-10 bg-card rounded-full"
              onClick={handlePaymentCancel}
            >
              <X className="w-5 h-5" />
            </Button>
            <PaymentMethodSelector
              api={api}
              rideId={activeRide.id}
              amount={activeRide.final_fare || activeRide.estimated_fare}
              rideName={`${activeRide.pickup?.address} → ${activeRide.destination?.address}`}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
              onError={handlePaymentError}
            />
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      <Dialog open={showAddCardModal} onOpenChange={setShowAddCardModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Enregistrer une carte bancaire
            </DialogTitle>
          </DialogHeader>
          <SavedCardManager 
            onCardSaved={handleCardSaved}
            showTitle={false}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PassengerDashboard;
